"use strict";

import NodeBase from "../util/NodeBase";
import { drawRoundRect } from "../util/shapes";

/**
 * A ToastUnit Node/Cluster shape.
 *
 * @augments ToastUnit
 */
class ToastUnit extends NodeBase {
  /**
   * @param {object} options
   * @param {object} body
   * @param {Label} labelModule
   * @param captionModule
   * @param iconsList
   */
  constructor(options, body, labelModule, captionModule, notationModule, iconsList) {
    super(options, body, labelModule, captionModule, notationModule);
    this._setMargins(labelModule);
    this.captionTextSize = { width: 0, height: 0 };
    this.iconsList = iconsList;

    this.width = 164; //dimensions.width + this.margin.right + this.margin.left;
    if (options.head && options.head.icon) {
      if (options.head.icon === 'data' || options.head.icon === 'model') {
        this.width = 100;
      }
    }
    this.height = 64; //dimensions.height + this.margin.top + this.margin.bottom;
    this.radius = 6; //this.width / 2;
    this.headwidth = 36;
    this.headicon = { width: 24, height: 24 };
    this.icon = { width: 12, height: 12, margin: 2};
    this.toolbar = [
      { item: "remove", enable: options.toolbar.remove,  left: 0, top: 0, right: 0, bottom: 0 },
      { item: "copy", enable: options.toolbar.copy, left: 0, top: 0, right: 0, bottom: 0 },
      { item: "link", enable: options.toolbar.link, left: 0, top: 0, right: 0, bottom: 0 },
      { item: "setting", enable: options.toolbar.setting, left: 0, top: 0, right: 0, bottom: 0 },
    ]

    const offset = 2;
    for (let i = 0, idx = 0, len = this.toolbar.length; i < len; i++) {
      if (!this.toolbar[i].enable) {
        continue;
      }
      idx++;
      // offset
      this.toolbar[i].left = this.width - (this.icon.width + this.icon.margin + this.icon.margin) * idx - offset;
      this.toolbar[i].right = this.toolbar[i].left + (this.icon.width + this.icon.margin + this.icon.margin);
      this.toolbar[i].top = this.height - (this.icon.width + this.icon.margin + this.icon.margin) - offset;
      this.toolbar[i].bottom = this.height - offset;
    }
  }

  /**
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {boolean} [selected]
   * @param {boolean} [hover]
   */
  resize(ctx, selected = this.selected, hover = this.hover) {
    if (this.needsRefresh(selected, hover)) {
      this.getDimensionsFromLabel(ctx, selected, hover);
      this.captionTextSize = this.captionModule.getTextSize(ctx, selected, hover);
    }
  }

  /**
   * @param {CanvasRenderingContext2D} ctx
   * @param {number} x width
   * @param {number} y height
   * @param {boolean} selected
   * @param {boolean} hover
   * @param {ArrowOptions} values
   * @param pointer
   */
  draw(ctx, x, y, selected, hover, values, pointer) {
    this.resize(ctx, selected, hover);
    this.left = x - this.width / 2;
    this.top = y - this.height / 2;

    this.initContextForDraw(ctx, values);
    drawRoundRect(ctx, this.left, this.top, this.width, this.height, this.radius);
    this.performFill(ctx, values);

    this.drawHead(ctx, this.left, this.top, this.headwidth, this.height, this.radius);
    values.color = this.options.head.background || "#ffffff";
    this.performFill(ctx, values);
    if (this.options.head && this.options.head.icon && this.iconsList[this.options.head.icon]) {
      ctx.drawImage(this.iconsList[this.options.head.icon].normal,
                    this.left + (this.headwidth - this.headicon.width) / 2,
                    this.top + (this.height - this.headicon.height) / 2,
                    this.headicon.width,
                    this.headicon.height);
    } else {
      ctx.drawImage(this.iconsList.none.normal,
                    this.left + (this.headwidth - this.headicon.width) / 2,
                    this.top + (this.height - this.headicon.height) / 2,
                    this.headicon.width,
                    this.headicon.height);
    }

    if (this.options.caption.enabled) {
      this.drawCaption(ctx, selected, hover);
    }

    if (this.options.caption.status && this.options.caption.status != "none") {
      if (this.options.caption.status == 'submit') {
        ctx.drawImage(this.iconsList.vmark.normal, this.left + this.width - 14 - 4, this.top + 4, 14, 14);
      } else if (this.options.caption.status == 'error') {
        ctx.drawImage(this.iconsList.xmark.normal, this.left + this.width - 14 - 4, this.top + 4, 14, 14);
      } else if (this.options.caption.status == 'unknown') {
        ctx.drawImage(this.iconsList.smark.normal, this.left + this.width - 14 - 4, this.top + 4, 14, 14);
      } else if (this.options.caption.status == 'running') {
        ctx.drawImage(this.iconsList.runningmark.normal, this.left + this.width - 14 - 4, this.top + 4, 14, 14);
      } else if (this.options.caption.status == 'pause') {
        ctx.drawImage(this.iconsList.pausemark.normal, this.left + this.width - 14 - 4, this.top + 4, 14, 14);
      } else if (this.options.caption.status == 'stop') {
        ctx.drawImage(this.iconsList.stopmark.normal, this.left + this.width - 14 - 4, this.top + 4, 14, 14);
      }
    }

    this.updateBoundingBox(x, y, ctx, selected, hover);
    this.drawLabel(ctx, selected, hover);
    if (values.useToolBar) {
      this.drawToolBar(ctx, pointer, selected, hover);
    }
  }

  /**
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {boolean} selected
   * @param {boolean} hover
   */
  drawHead(ctx, x, y, w, h, r) {
    const r2d = Math.PI / 180;
    if (w - 2 * r < 0) {
      r = w / 2;
    } //ensure that the radius isn't too large for x
    if (h - 2 * r < 0) {
      r = h / 2;
    } //ensure that the radius isn't too large for y
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w, y);
    ctx.lineTo(x + w, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.arc(x + r, y + h - r, r, r2d * 90, r2d * 180, false);
    ctx.lineTo(x, y + r);
    ctx.arc(x + r, y + r, r, r2d * 180, r2d * 270, false);
    ctx.closePath();
  }

  /**
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {boolean} selected
   * @param {boolean} hover
   */
  drawCaption(ctx, selected, hover) {
    if (this.options.caption.label) {
      const height = (this.options.caption.height > this.captionTextSize.height ? this.options.caption.height : this.captionTextSize.height);
      let ls = this.left + this.headwidth + 4;
      let ts = this.top + this.margin.top + 8;
      this.captionModule.draw(ctx, ls, ts, selected, hover, "middle", "left");
    }
  }

  /**
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {boolean} selected
   * @param {boolean} hover
   */
  drawLabel(ctx, selected, hover) {
    let ls = this.left + this.headwidth + 4;

    const capH = this.options.caption.height + this.options.caption.margin.top + this.options.caption.margin.bottom;
    let ts = (this.top + this.height / 2); //this.top + capH + 8 - this.textSize.height / 2;

    this.labelModule.draw(ctx, ls, ts, selected, hover, "middle", "left");
  }

  /**
   * @param {CanvasRenderingContext2D} ctx
   * @param pointer
   * @param {boolean} selected
   * @param {boolean} hover
   */
  drawToolBar(ctx, pointer, selected, hover) {
    if (!selected) {
      return;
    }
    for (let i = 0, state = "normal", len = this.toolbar.length; i < len; i++, state = "normal") {
      if (!this.toolbar[i].enable || !(this.iconsList[this.toolbar[i].item])) {
        continue;
      }
      if (pointer.x >= this.left + this.toolbar[i].left && pointer.x <= this.left + this.toolbar[i].right &&
          pointer.y >= this.top + this.toolbar[i].top && pointer.y <= this.top + this.toolbar[i].bottom) {
          state = "hover";
      }

      ctx.drawImage(this.iconsList[this.toolbar[i].item][state],
                    this.left + this.toolbar[i].left + this.icon.margin,
                    this.top + this.toolbar[i].top + this.icon.margin,
                    this.icon.width,
                    this.icon.height);
    }
  }

  /**
   *
   * @param {number} x width
   * @param {number} y height
   * @param {CanvasRenderingContext2D} ctx
   * @param {boolean} selected
   * @param {boolean} hover
   */
  updateBoundingBox(x, y, ctx, selected, hover) {
    this._updateBoundingBox(x, y, ctx, selected, hover);

    const borderRadius = this.options.shapeProperties.borderRadius; // only effective for box
    this._addBoundingBoxMargin(borderRadius);
  }

  /**
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {number} angle
   * @returns {number}
   */
  distanceToBorder(ctx, angle) {
    if (ctx) {
      this.resize(ctx);
    }
    const borderWidth = this.options.borderWidth;

    return (
      Math.min(
        Math.abs(this.width / 2 / Math.cos(angle)),
        Math.abs(this.height / 2 / Math.sin(angle))
      ) + borderWidth
    );
  }

  /**
   * @param {x: number, y: number} pointer
   */
  hitActionItem(pointer) {
    for (let i = 0, len = this.toolbar.length; i < len; i++) {
      if (!this.toolbar[i].enable) {
        continue;
      }
      if (pointer.x >= this.left + this.toolbar[i].left && pointer.x <= this.left + this.toolbar[i].right &&
        pointer.y >= this.top + this.toolbar[i].top && pointer.y <= this.top + this.toolbar[i].bottom) {
        return this.toolbar[i].item;
      }
    }

    return "";
  }

  /**
   * @param {x: number, y: number} pointer
   */
  overActionItemName(pointer) {
    return this.hitActionItem(pointer);
  }
}

export default ToastUnit;
